import { useLingui } from "@lingui/react";
import { useFormatters } from "./useFormatters";
import { MetricField, MetricUnit } from "@/__generated__/graphql";
import { msg } from "@lingui/macro";
import { EChartsOptionBuilder } from "@features/echarts";
import { useMemo } from "react";

interface MetricData {
  data: [number, number][] | null;
  unit: MetricUnit | null;
  precision: number | null;
}

interface MinichartData {
  flow?: MetricData;
  level?: MetricData
  solarV?: MetricData;
  batterySoc?: MetricData;
  availableFields: MetricField[];
}

interface MetricConfig {
  field: MetricField;
  yAxisId: string;
  name: ReturnType<typeof msg>;
  seriesType: 'line' | 'area' | 'bar';
  gridPosition: 'left' | 'right';
  yAxisOptions?: {
    max?: number;
    min?: number;
  };
}

export const METRICS_CONFIG: MetricConfig[] = [
  {
    field: MetricField.Flow,
    yAxisId: 'flow',
    name: msg`Caudal`,
    seriesType: 'line',
    gridPosition: 'left',
  },
  {
    field: MetricField.Level,
    yAxisId: 'level',
    name: msg`Nivel`,
    seriesType: 'line',
    gridPosition: 'left',
  },
  {
    field: MetricField.BatterySoc,
    yAxisId: 'battery',
    name: msg`Batería`,
    seriesType: 'line',
    gridPosition: 'right',
    yAxisOptions: {
      max: 100,
      min: 0,
    }
  },
]

interface ProcessedMetric {
  config: MetricConfig;
  data: [number, number][];
  metricData: MetricData;
}

interface UseMinichartOptionsConfig {
  limitPoints?: number;
  showGrid?: 'both' | 'left' | 'right' | 'single';
  enableTooltip?: boolean;
  enableZoom?: boolean;
}

export const useMinichartOptions = (
  data: MinichartData,
  config: UseMinichartOptionsConfig = {}
) => {
  const { formatMetricValue } = useFormatters();
  const { _, i18n } = useLingui();

  const {
    limitPoints = 10,
    enableTooltip = true,
    enableZoom = false,
  } = config;

  const option = useMemo(() => {
    const { availableFields } = data;

    const processedMetrics: ProcessedMetric[] = METRICS_CONFIG
      .filter(metricConfig => {
        const metricData = data[metricConfig.field.toLowerCase()];
        return (
          metricData?.data &&
          metricData.data.length > 0 &&
          availableFields.includes(metricConfig.field)
        );
      })
      .map(metricConfig => {
        const metricData = data[metricConfig.field.toLowerCase()];
        let processedData = [...metricData.data];

        if (limitPoints > 0 && processedData.length > limitPoints) {
          const startIndex = processedData.length - limitPoints;
          processedData = processedData.slice(startIndex);
        }
        
        return {
          config: metricConfig,
          data: processedData,
          metricData,
        };
      });

    if (processedMetrics.length === 0) {
      return null;
    }

    const hasLeft = processedMetrics.some(m => m.config.gridPosition === 'left');
    const hasRight = processedMetrics.some(m => m.config.gridPosition === 'right');
    const gridConfig = hasLeft && hasRight ? 'both' : hasLeft ? 'left' : 'right';

    const option = new EChartsOptionBuilder({
      override: (base) => ({
        ...base,
        axisPointer: {
          ...base.axisPointer,
          link: []
        },
        dataZoom: enableZoom ? base.dataZoom : [],
        grid:
          gridConfig === 'both'
            ? [
                {
                  id: 'left',
                  top: 12,
                  left: 0,
                  right: '51%',
                  bottom: 10,
                  containLabel: true,
                },
                {
                  id: 'right',
                  top: 12,
                  left: '51%',
                  right: 0,
                  bottom: 10,
                  containLabel: true,
                },
              ]
            : {
              top: 12,
              left: 0,
              right: 0,
              bottom: 10,
              containLabel: true,
            },
        legend: undefined,
      }),
      toolbox: { disabled: true },
      tooltip: enableTooltip
        ? {
          confine: true,
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            crossStyle: {
              color: '#999',
            },
            animation: false,
          },
          position: (point, _, __, ___, size) => {
            const [width] = size.viewSize;
            const isRight = point[0] > width / 2;
            const offset = 40;
            return {
              top: point[1],
              left: isRight ? 'auto' : point[0] + offset,
              right: isRight ? width - point[0] + offset : 'auto',
            };
          },
          formatter(paramsList) {
            const params = Array.isArray(paramsList) ? paramsList : [paramsList];

            return params
              .map((param) => {
                let unit = MetricUnit.None,
                  precision = 1;
                if (param.seriesIndex != null) {
                  const yAxisId = option.getYAxisIdFromSerieId(param.seriesId);

                  if (yAxisId) {
                    unit = (option.getSerieUnit(yAxisId) as MetricUnit) ?? MetricUnit.None;
                    precision = option.getSeriePrecision(yAxisId);
                  }
                }
                const data = param.value as [number, number];
                const value = formatMetricValue(data[1], { unit, precision });
                return `${param.marker} ${param.seriesName}: ${value}`;
              })
              .join('<br>');
          },
          backgroundColor: '#222b3688',
          borderColor: '#ffffff88',
          textStyle: {
            color: '#fff',
          },
          extraCssText: 'line-height: 1.4;',
        }
        : undefined,
    });

    option.addXAxis(
      {
        id: 'time',
        type: 'time',
        gridIndex: 0,
      },
      (base) => ({
        ...base,
        max: Date.now(),
        splitNumber: 2,
      })
    );

    if (gridConfig === 'both') {
      option.addXAxis(
        {
          id: 'time-right',
          type: 'time',
          gridIndex: 1,
        },
        (base) => ({
          ...base,
          max: Date.now(),
          splitNumber: 2,
          hideOverlap: true,
        })
      );
    }
      

    processedMetrics.forEach(({ config: metricConfig, data: metricData, metricData: originalData }) => {
      const gridIndex = gridConfig === 'both' && metricConfig.gridPosition === 'right' ? 1 : 0;

      option.addYAxis(
        {
          id: metricConfig.yAxisId,
          type: 'value',
          unit: originalData.unit,
          precision: originalData.precision,
          gridIndex,
        },
        (base) => ({
          ...base,
          splitNumber: 2,
          axisPointer: { show: false },
          axisLabel: {
            ...base.axisLabel,
            inside: false,
          },
          ...(metricConfig.yAxisOptions || {})
        })
      );

      option.addSeries([
        {
          meta: {
            id: metricConfig.field.toLowerCase(),
            name: _(metricConfig.name),
            type: metricConfig.seriesType,
            dataType: metricConfig.field.toLowerCase(),
            xAxisId: gridConfig === 'both' && metricConfig.gridPosition === 'right' ? 'time-right': 'time',
            yAxisId: metricConfig.yAxisId,
            data: metricData,
          },
          value: (base) => ({
            ...base,
            symbol: 'circle',
          }),
        },
      ]);
    });
    
    return option;
  }, [data, formatMetricValue, i18n, limitPoints, enableTooltip, enableZoom]);

  return option;
}